import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Building2, Cpu, Factory, Fuel, Waves } from "lucide-react";
import { TEST_IDS } from "../constants/testIds";
import GlobalMap from "../components/site/GlobalMap";

const SECTORS = [
  { icon: Fuel, name: "Energy", copy: "Crude, LNG, LPG, refining, pipelines, and clean power at institutional scale." },
  { icon: Building2, name: "Infrastructure", copy: "Ports, terminals, transmission, and public-private partnerships across three continents." },
  { icon: Factory, name: "Commodities", copy: "Petrochemicals, supply-chain finance, and cross-border procurement." },
  { icon: Cpu, name: "Technology", copy: "AI-ready data centers, sovereign compute, and enterprise transformation." },
  { icon: Waves, name: "Real Assets", copy: "ESG-aligned mixed-use, industrial, and long-duration real estate." },
];

const INSIGHTS = [
  {
    tag: "Perspective",
    title: "Institutional Capital in the AI Infrastructure Cycle",
    date: "Feb 2026",
    excerpt: "Why hyperscale power and interconnection are the defining constraint of the next decade.",
  },
  {
    tag: "Market Note",
    title: "Shandong Independents and the Global Crude Book",
    date: "Jan 2026",
    excerpt: "How China's independent refiners are recalibrating around Brent-linked supply agreements.",
  },
  {
    tag: "Diligence",
    title: "West African Solar: Underwriting Sovereign Risk",
    date: "Dec 2025",
    excerpt: "A framework for PPA-anchored capital in Ghana, Cote d'Ivoire, and Senegal.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1772187693438-12e4b8ef3a29?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxnbG9iYWwlMjBpbmZyYXN0cnVjdHVyZSUyMHNreWxpbmV8ZW58MHx8fHwxNzgzMjc1MzU3fDA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/85 via-[#0B1F3A]/75 to-[#0B1F3A]/95" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-32 md:pt-32 md:pb-44">
          <motion.h1
            initial="hidden" animate="show" variants={fadeUp} custom={1}
            className="font-display text-white text-[42px] md:text-[72px] lg:text-[86px] leading-[1.02] tracking-tight max-w-5xl"
          >
            Capitalizing on <em className="text-[#D4AF37] not-italic font-semibold">global horizons</em> — with institutional discipline.
          </motion.h1>
          <motion.p
            initial="hidden" animate="show" variants={fadeUp} custom={2}
            className="mt-8 max-w-2xl text-[#F8F7F2]/80 text-base md:text-lg leading-relaxed"
          >
            First Providence Capital connects global capital with strategic opportunities across
            energy, infrastructure, technology, and real assets — from Providence to Beijing,
            Lagos to Riyadh.
          </motion.p>
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={3} className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/strategies"
              data-testid={TEST_IDS.home.heroCta}
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[#c19e2f] transition"
            >
              Explore Strategies <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid={TEST_IDS.home.heroSecondary}
              className="inline-flex items-center gap-3 border border-[#F8F7F2]/50 text-[#F8F7F2] px-8 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
            >
              Contact the Firm
            </Link>
          </motion.div>
        </div>

        {/* Stat rail */}
        <div className="relative">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="bg-[#F8F7F2] border-t-4 border-[#D4AF37] grid grid-cols-2 md:grid-cols-4 divide-x divide-[#0B1F3A]/10 md:-mb-16 relative z-10 shadow-[0_20px_50px_-30px_rgba(11,31,58,0.4)]">
              <Stat label="Active Capital in Motion" value="$1.5B+" tid={TEST_IDS.home.statAum} />
              <Stat label="Live Projects" value="12" tid={TEST_IDS.home.statProjects} />
              <Stat label="Continents" value="4" tid={TEST_IDS.home.statGeo} />
              <Stat label="Sector Verticals" value="5" />
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 md:pt-40 pb-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="eyebrow">Our Philosophy</div>
          <div className="hairline mt-6" />
        </div>
        <div className="md:col-span-8">
          <p className="font-display text-3xl md:text-4xl leading-tight text-[#0B1F3A]">
            We believe exceptional investment performance is achieved through disciplined
            underwriting, operational excellence, transparent governance, and long-term
            strategic partnerships.
          </p>
          <p className="mt-8 text-body max-w-2xl">
            Our objective extends beyond capital deployment. We seek to build resilient
            enterprises, strengthen critical infrastructure, advance sustainable economic
            development, and generate compelling risk-adjusted returns for our investors
            and partners worldwide.
          </p>
        </div>
      </section>

      {/* SECTORS */}
      <section className="bg-[#0B1F3A] text-[#F8F7F2] py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row justify-between items-start gap-6 mb-16">
            <div>
              <div className="eyebrow" style={{ color: "#D4AF37" }}>Sector Verticals</div>
              <h2 className="font-display text-4xl md:text-5xl mt-4 max-w-xl">Five sectors, one disciplined thesis.</h2>
            </div>
            <Link to="/strategies" className="text-[#D4AF37] text-xs uppercase tracking-[0.24em] gold-underline">
              All strategies →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#F8F7F2]/10">
            {SECTORS.map((s, i) => (
              <motion.div
                key={s.name}
                data-testid={`${TEST_IDS.home.sectorCard}-${s.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.6 }}
                className="p-8 md:p-10 bg-[#0B1F3A] hover:bg-[#0F2646] transition group min-h-[240px] flex flex-col"
              >
                <s.icon size={28} strokeWidth={1.4} className="text-[#D4AF37]" />
                <h3 className="font-display text-2xl mt-6">{s.name}</h3>
                <p className="text-sm text-[#F8F7F2]/70 mt-3 leading-relaxed">{s.copy}</p>
                <div className="mt-auto pt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] opacity-0 group-hover:opacity-100 transition">
                  Explore <ArrowUpRight size={12} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL MAP */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-5">
              <div className="eyebrow">Global Reach</div>
              <h2 className="font-display text-4xl md:text-5xl mt-4">A firm built for cross-border execution.</h2>
            </div>
            <div className="md:col-span-7 flex items-end">
              <p className="text-body">
                Direct execution capabilities in Asia through our Beijing-based leadership,
                institutional governance from the United States, and long-standing relationships
                across West Africa and the Middle East. Every mandate is anchored by disciplined
                risk management and enduring partnerships.
              </p>
            </div>
          </div>
          <GlobalMap />
        </div>
      </section>

      {/* INSIGHTS */}
      <section className="py-24 bg-[#F8F7F2] border-t border-[#0B1F3A]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row justify-between items-baseline gap-4 mb-12">
            <div>
              <div className="eyebrow">Insights & Perspectives</div>
              <h2 className="font-display text-4xl md:text-5xl mt-4">Editorial-grade thinking from our team.</h2>
            </div>
            <Link to="/insights" className="text-xs uppercase tracking-[0.24em] text-[#0B1F3A] gold-underline">
              All perspectives →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {INSIGHTS.map((art, i) => (
              <motion.article
                key={i}
                data-testid={`${TEST_IDS.home.insightCard}-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="bg-white border border-[#0B1F3A]/10 p-8 hover:shadow-lg hover:-translate-y-1 transition group"
              >
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em]">
                  <span className="text-[#3399FF]">{art.tag}</span>
                  <span className="text-[#5F6B7A]">{art.date}</span>
                </div>
                <h3 className="font-display text-2xl mt-6 leading-snug text-[#0B1F3A]">{art.title}</h3>
                <p className="mt-4 text-sm text-[#5F6B7A] leading-relaxed">{art.excerpt}</p>
                <div className="mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                  Read <ArrowUpRight size={12} />
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0B1F3A] text-[#F8F7F2]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
          <div className="eyebrow" style={{ color: "#D4AF37" }}>Institutional Access</div>
          <h2 className="font-display text-4xl md:text-6xl mt-4 leading-tight">
            Ready to explore a partnership?
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-[#F8F7F2]/75">
            Our team welcomes conversations with qualified investors, developers, and institutional
            counterparties. Reach out to schedule a confidential briefing.
          </p>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to="/contact" className="bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[#c19e2f]">
              Request a briefing
            </Link>
            <Link to="/login" className="border border-[#F8F7F2]/40 text-[#F8F7F2] px-8 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:border-[#D4AF37] hover:text-[#D4AF37]">
              Investor Portal
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, tid }) {
  return (
    <div className="p-6 md:p-10" data-testid={tid}>
      <div className="numeral text-3xl md:text-5xl text-[#0B1F3A] leading-none whitespace-nowrap">{value}</div>
      <div className="mt-3 md:mt-2 text-[10px] uppercase tracking-[0.24em] text-[#5F6B7A]">{label}</div>
    </div>
  );
}
